// Export comptable (journal des ventes, journal des règlements) — lecture des
// factures et des règlements, réglages des comptes, numéros de compte client,
// marquage de ce qui a été exporté ou pointé.
//
// Le raisonnement (écritures, équilibre, format du fichier) vit dans
// @imp/shared/export-comptable et @imp/shared/journal-reglements, purs et
// testés contre le fichier du comptable. Ici on ne fait que lire la base et
// écrire quatre choses : les réglages (organizations.parametres), le code
// comptable d'un client (contacts.code_comptable), la trace d'export sur une
// facture (factures.metadata — champ que la loi anti-fraude laisse modifiable)
// ou sur un règlement (reglements.export_lot), et le pointage d'un règlement.

import {
  codeClientSuivant,
  type ComptesExport,
  COMPTES_PAR_DEFAUT,
  encoderWindows1252,
  type FactureExport,
} from '@imp/shared/export-comptable';
import { type FactureEnAttente, type ReglementExport } from '@imp/shared/journal-reglements';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { type Contact, contactDisplayName, ORG_ID } from '@/modules/clients/api';

export type Encodage = 'windows-1252' | 'utf-8';

export interface ReglagesExportComptable extends ComptesExport {
  encodage: Encodage;
}

export const REGLAGES_PAR_DEFAUT: ReglagesExportComptable = {
  ...COMPTES_PAR_DEFAUT,
  encodage: 'windows-1252',
};

interface ParametresOrg {
  exportComptable?: Partial<ReglagesExportComptable>;
  [cle: string]: unknown;
}

async function lireParametres(): Promise<ParametresOrg> {
  const { data, error } = await supabase
    .from('organizations')
    .select('parametres')
    .eq('id', ORG_ID)
    .single();
  if (error) throw new Error(error.message);
  const p = (data as { parametres: unknown } | null)?.parametres;
  return p && typeof p === 'object' ? (p as ParametresOrg) : {};
}

export async function getReglagesExport(): Promise<ReglagesExportComptable> {
  const p = await lireParametres();
  const r = p.exportComptable ?? {};
  return {
    ...REGLAGES_PAR_DEFAUT,
    ...r,
    ventesParKind: { ...REGLAGES_PAR_DEFAUT.ventesParKind, ...(r.ventesParKind ?? {}) },
    tvaParTaux: { ...REGLAGES_PAR_DEFAUT.tvaParTaux, ...(r.tvaParTaux ?? {}) },
  };
}

export async function setReglagesExport(r: ReglagesExportComptable): Promise<void> {
  // Les autres réglages de l'organisation (délais, relances…) vivent dans le
  // même objet : on relit, on fusionne, on réécrit — jamais on n'écrase.
  const p = await lireParametres();
  const { error } = await supabase
    .from('organizations')
    .update({ parametres: { ...p, exportComptable: r } })
    .eq('id', ORG_ID);
  if (error) throw new Error(error.message);
}

type ContactNom = Pick<Contact, 'type_personne' | 'nom' | 'prenom' | 'raison_sociale'>;
type ContactCompte = ContactNom & { id: string; code_comptable: string | null };

// ----- Numéros de compte client ---------------------------------------------

/** Les clients sans numéro de compte, dans l'ordre où ils sont apparus. */
export async function listClientsSansCode(): Promise<(ContactCompte & { kind: string })[]> {
  return lireTout<ContactCompte & { kind: string }>((de, a) =>
    supabase
      .from('contacts')
      .select('id,kind,type_personne,nom,prenom,raison_sociale,code_comptable')
      .eq('organization_id', ORG_ID)
      .is('deleted_at', null)
      .is('code_comptable', null)
      .in('kind', ['client', 'prospect'])
      .order('created_at')
      .range(de, a),
  );
}

async function codesExistants(): Promise<string[]> {
  const lignes = await lireTout<{ code_comptable: string | null }>((de, a) =>
    supabase
      .from('contacts')
      .select('code_comptable')
      .eq('organization_id', ORG_ID)
      .not('code_comptable', 'is', null)
      .order('code_comptable')
      .range(de, a),
  );
  return lignes.map((l) => l.code_comptable).filter((c): c is string => c !== null);
}

/**
 * Attribue un numéro aux contacts donnés qui n'en ont pas, à la suite du plus
 * grand déjà pris. Un numéro n'est jamais repris : le comptable a pu l'utiliser.
 * Rend le nombre de numéros attribués.
 */
export async function attribuerCodesManquants(
  contactIds: string[],
  comptes: Pick<ComptesExport, 'prefixeClient' | 'chiffresClient'>,
): Promise<number> {
  if (contactIds.length === 0) return 0;
  const { data, error } = await supabase
    .from('contacts')
    .select('id,code_comptable')
    .in('id', contactIds);
  if (error) throw new Error(error.message);
  const sans = (data as { id: string; code_comptable: string | null }[]).filter(
    (c) => c.code_comptable === null,
  );
  if (sans.length === 0) return 0;
  const pris = await codesExistants();
  let n = 0;
  for (const c of sans) {
    const code = codeClientSuivant(pris, comptes.prefixeClient, comptes.chiffresClient);
    const { error: e } = await supabase
      .from('contacts')
      .update({ code_comptable: code })
      .eq('id', c.id)
      .is('code_comptable', null);
    if (e) throw new Error(`client ${c.id} : ${e.message}`);
    pris.push(code);
    n += 1;
  }
  return n;
}

// ----- Factures --------------------------------------------------------------

/** Une facture prête à passer en écritures, avec ce que l'écran doit montrer. */
export interface FactureExportable extends FactureExport {
  id: string;
  statut: string;
  contactId: string | null;
  /** Nom du dernier fichier dans lequel elle est partie, null si jamais exportée. */
  exporteeDans: string | null;
  exporteeLe: string | null;
}

interface LigneBrute {
  libelle: string;
  type: string;
  total_ht: string | number;
  ordre: number;
  taux_tva: { taux: string | number } | null;
  articles: { kind: string } | null;
}

interface FactureBrute {
  id: string;
  numero: string;
  kind: string;
  statut: string;
  contact_id: string | null;
  date_emission: string;
  montant_ht: string | number;
  montant_tva: string | number;
  montant_ttc: string | number;
  detail_tva: { tauxValeur: number; baseHt: number; montantTva: number }[] | null;
  metadata: { export_comptable?: { lot?: string; date?: string } } | null;
  contacts: ContactCompte | null;
  factures_lignes: LigneBrute[];
}

/** Un taux stocké en fraction (0,10) devient un pourcentage (10) ; déjà en pourcentage, il reste. */
const enPourcentage = (t: number): number => (t > 0 && t <= 1 ? t * 100 : t);

/**
 * Les factures ÉMISES de la période (jamais les brouillons : ils n'ont ni
 * numéro définitif ni montants figés), avec leurs lignes et leur client.
 */
export async function listFacturesExportables(
  debut: string,
  fin: string,
): Promise<FactureExportable[]> {
  const { data, error } = await supabase
    .from('factures')
    .select(
      'id,numero,kind,statut,contact_id,date_emission,montant_ht,montant_tva,montant_ttc,detail_tva,metadata,contacts(id,type_personne,nom,prenom,raison_sociale,code_comptable),factures_lignes(libelle,type,total_ht,ordre,taux_tva(taux),articles(kind))',
    )
    .eq('organization_id', ORG_ID)
    .is('deleted_at', null)
    .neq('statut', 'brouillon')
    .gte('date_emission', debut)
    .lte('date_emission', fin)
    .order('date_emission')
    .order('numero');
  if (error) throw new Error(error.message);
  const brutes = data as unknown as FactureBrute[];
  return brutes.map((f) => ({
    id: f.id,
    numero: f.numero,
    statut: f.statut,
    kind: f.kind,
    contactId: f.contact_id,
    dateEmission: f.date_emission,
    client: f.contacts ? contactDisplayName(f.contacts) : '(client inconnu)',
    compteClient: f.contacts?.code_comptable ?? null,
    lignes: [...f.factures_lignes]
      .sort((a, b) => a.ordre - b.ordre)
      .filter((l) => l.type === 'article' || l.type === '')
      .map((l) => ({
        libelle: l.libelle,
        totalHt: Number(l.total_ht),
        tauxTva: l.taux_tva ? enPourcentage(Number(l.taux_tva.taux)) : null,
        kind: l.articles?.kind ?? null,
      })),
    detailTva: (f.detail_tva ?? []).map((d) => ({
      taux: enPourcentage(d.tauxValeur),
      baseHt: d.baseHt,
      montantTva: d.montantTva,
    })),
    montantHt: Number(f.montant_ht),
    montantTva: Number(f.montant_tva),
    montantTtc: Number(f.montant_ttc),
    exporteeDans: f.metadata?.export_comptable?.lot ?? null,
    exporteeLe: f.metadata?.export_comptable?.date ?? null,
  }));
}

/**
 * Garde la trace de l'export sur chaque facture, pour pouvoir n'exporter
 * ensuite que ce qui ne l'a jamais été — comme Extrabat le fait.
 */
export async function marquerExportees(ids: string[], lot: string): Promise<void> {
  if (ids.length === 0) return;
  const { data, error } = await supabase.from('factures').select('id,metadata').in('id', ids);
  if (error) throw new Error(error.message);
  const date = new Date().toISOString();
  const lignes = data as { id: string; metadata: Record<string, unknown> | null }[];
  for (const l of lignes) {
    const { error: e } = await supabase
      .from('factures')
      .update({ metadata: { ...(l.metadata ?? {}), export_comptable: { lot, date } } })
      .eq('id', l.id);
    if (e) throw new Error(`facture ${l.id} : ${e.message}`);
  }
}

// ----- Règlements ------------------------------------------------------------

export interface ReglementJournal extends ReglementExport {
  pointe: boolean;
  pointeLe: string | null;
  exportLot: string | null;
  exportLe: string | null;
  contactId: string | null;
}

interface ReglementBrut {
  id: string;
  date_reglement: string;
  mode: string;
  montant: string | number;
  reference: string | null;
  pointe: boolean;
  pointe_le: string | null;
  export_lot: string | null;
  export_le: string | null;
  factures: {
    numero: string;
    kind: string;
    contact_id: string | null;
    contacts: ContactCompte | null;
  } | null;
}

/** Les règlements reçus sur la période, avec leur facture et leur client. */
export async function listReglementsPeriode(debut: string, fin: string): Promise<ReglementJournal[]> {
  const lignes = await lireTout<ReglementBrut>((de, a) =>
    supabase
      .from('reglements')
      .select(
        'id,date_reglement,mode,montant,reference,pointe,pointe_le,export_lot,export_le,factures(numero,kind,contact_id,contacts(id,type_personne,nom,prenom,raison_sociale,code_comptable))',
      )
      .eq('organization_id', ORG_ID)
      .is('deleted_at', null)
      .gte('date_reglement', debut)
      .lte('date_reglement', fin)
      .order('date_reglement')
      .order('id')
      .range(de, a)
      .overrideTypes<ReglementBrut[], { merge: false }>(),
  );
  return lignes.map((r) => ({
    id: r.id,
    dateReglement: r.date_reglement,
    mode: r.mode,
    montant: Number(r.montant),
    reference: r.reference,
    factureNumero: r.factures?.numero ?? '(facture inconnue)',
    factureKind: r.factures?.kind ?? 'standard',
    client: r.factures?.contacts ? contactDisplayName(r.factures.contacts) : '(client inconnu)',
    compteClient: r.factures?.contacts?.code_comptable ?? null,
    contactId: r.factures?.contact_id ?? null,
    pointe: r.pointe,
    pointeLe: r.pointe_le,
    exportLot: r.export_lot,
    exportLe: r.export_le,
  }));
}

/** Coche ou décoche un règlement : retrouvé (ou non) sur le relevé bancaire. */
export async function pointerReglement(id: string, pointe: boolean): Promise<void> {
  const { error } = await supabase
    .from('reglements')
    .update({ pointe, pointe_le: pointe ? new Date().toISOString() : null })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function marquerReglementsExportes(ids: string[], lot: string): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await supabase
    .from('reglements')
    .update({ export_lot: lot, export_le: new Date().toISOString() })
    .in('id', ids);
  if (error) throw new Error(error.message);
}

interface FactureAttenteBrute {
  id: string;
  numero: string;
  kind: string;
  statut: string;
  contact_id: string | null;
  date_emission: string;
  date_echeance: string;
  montant_ttc: string | number;
  montant_paye: string | number;
  contacts: ContactNom | null;
}

/** Toutes les factures émises non soldées : ce qu'il reste à encaisser. */
export async function listFacturesEnAttente(): Promise<(FactureEnAttente & { contactId: string | null })[]> {
  const lignes = await lireTout<FactureAttenteBrute>((de, a) =>
    supabase
      .from('factures')
      .select(
        'id,numero,kind,statut,contact_id,date_emission,date_echeance,montant_ttc,montant_paye,contacts(type_personne,nom,prenom,raison_sociale)',
      )
      .eq('organization_id', ORG_ID)
      .is('deleted_at', null)
      .not('statut', 'in', '("brouillon","payee","annulee_avoir")')
      .neq('kind', 'avoir')
      .order('date_echeance')
      .range(de, a)
      .overrideTypes<FactureAttenteBrute[], { merge: false }>(),
  );
  return lignes.map((f) => ({
    id: f.id,
    numero: f.numero,
    kind: f.kind,
    statut: f.statut,
    contactId: f.contact_id,
    client: f.contacts ? contactDisplayName(f.contacts) : '(client inconnu)',
    dateEmission: f.date_emission,
    dateEcheance: f.date_echeance,
    montantTtc: Number(f.montant_ttc),
    montantPaye: Number(f.montant_paye),
  }));
}

// ----- Téléchargement ----------------------------------------------------------

/** Déclenche le téléchargement d'un fichier texte, dans l'encodage voulu. */
export function telechargerCsv(nom: string, contenu: string, encodage: Encodage): void {
  const octets = encodage === 'utf-8' ? new TextEncoder().encode(contenu) : encoderWindows1252(contenu);
  // Copie dans un tampon neuf : TypeScript veut un ArrayBuffer ferme pour un Blob.
  const tampon = new Uint8Array(octets.length);
  tampon.set(octets);
  const blob = new Blob([tampon], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
